import * as React from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { cn } from "../../utils/classMerger";

interface SwitchProps
    extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> {
    checkedLabel?: string;
    uncheckedLabel?: string;
}

const Switch = React.forwardRef<
    React.ElementRef<typeof SwitchPrimitives.Root>,
    SwitchProps
>(({ className, checked, defaultChecked, onCheckedChange, checkedLabel = "ON", uncheckedLabel = "OFF", ...props }, ref) => {
    // track internal state for uncontrolled usage
    const [internalChecked, setInternalChecked] = React.useState(
        defaultChecked ?? false
    );
    const isControlled = checked !== undefined;
    const state = isControlled ? checked! : internalChecked;

    const handleCheckedChange = (value: boolean) => {
        if (!isControlled) {
            setInternalChecked(value);
        }
        onCheckedChange?.(value);
    };

    return (
        <label className="inline-flex items-center space-x-2">
            {/* Reserve the width of the longer label so toggling does not shift adjacent content */}
            <span className="grid justify-items-end text-sm font-semibold">
                <span
                    aria-hidden
                    className="invisible col-start-1 row-start-1"
                >
                    {checkedLabel.length >= uncheckedLabel.length
                        ? checkedLabel
                        : uncheckedLabel}
                </span>
                <span
                    className={`col-start-1 row-start-1 ${
                        state ? "text-[#45BF55]" : "text-[#4B5563]"
                    }`}
                >
                    {state ? checkedLabel : uncheckedLabel}
                </span>
            </span>
            <SwitchPrimitives.Root
                className={cn(
                    "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-[#45BF55] data-[state=unchecked]:bg-[#4B5563]",
                    className
                )}
                checked={checked}
                defaultChecked={defaultChecked}
                onCheckedChange={handleCheckedChange}
                {...props}
                ref={ref}
            >
                <SwitchPrimitives.Thumb
                    className={cn(
                        "pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
                    )}
                />
            </SwitchPrimitives.Root>
        </label>
    );
});

Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
